import React from "react";
import {
  Box,
  Grid,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import {
  VerifiedUser,
  Speed,
  Engineering,
  Favorite,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { SellerReview } from "../services/sellerReviews";

export type ReviewCriteriaField =
  | "reliability_rating"
  | "response_speed_rating"
  | "engineer_efficiency_rating"
  | "honesty_rating";

export const REVIEW_CRITERIA: {
  field: ReviewCriteriaField;
  labelKey: string;
  labelDefault: string;
  Icon: typeof VerifiedUser;
}[] = [
  {
    field: "reliability_rating",
    labelKey: "reviews.reliability",
    labelDefault: "Reliability",
    Icon: VerifiedUser,
  },
  {
    field: "response_speed_rating",
    labelKey: "reviews.responseSpeed",
    labelDefault: "Response Speed",
    Icon: Speed,
  },
  {
    field: "engineer_efficiency_rating",
    labelKey: "reviews.efficiency",
    labelDefault: "Efficiency",
    Icon: Engineering,
  },
  {
    field: "honesty_rating",
    labelKey: "reviews.honesty",
    labelDefault: "Honesty",
    Icon: Favorite,
  },
];

const gridContainerSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  gap: 1.25,
  p: 1.5,
  borderRadius: 3,
  border: "1px solid",
};

interface ReviewCriteriaItemProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  showScore?: boolean;
}

export const ReviewCriteriaItem: React.FC<ReviewCriteriaItemProps> = ({
  icon,
  label,
  value,
  showScore = true,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: 1.25,
        borderRadius: 2.5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.12),
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              "& svg": { fontSize: 16 },
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: "0.78rem",
              lineHeight: 1.3,
              color: "text.primary",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {label}
          </Typography>
        </Stack>
        {showScore && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: "primary.main",
              flexShrink: 0,
              fontSize: "0.72rem",
            }}
          >
            {value.toFixed(1)}
          </Typography>
        )}
      </Stack>
      <Rating
        value={value}
        readOnly
        size="small"
        precision={0.5}
        sx={{ fontSize: "1rem", alignSelf: "flex-start" }}
      />
    </Box>
  );
};

interface ReviewCriteriaGridProps {
  review: Pick<
    SellerReview,
    | "reliability_rating"
    | "response_speed_rating"
    | "engineer_efficiency_rating"
    | "honesty_rating"
  >;
  showScore?: boolean;
}

export const ReviewCriteriaGrid: React.FC<ReviewCriteriaGridProps> = ({
  review,
  showScore = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...gridContainerSx,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderColor: alpha(theme.palette.primary.main, 0.08),
      }}
    >
      {REVIEW_CRITERIA.map(({ field, labelKey, labelDefault, Icon }) => (
        <ReviewCriteriaItem
          key={field}
          icon={<Icon fontSize="small" />}
          label={t(labelKey, { defaultValue: labelDefault })}
          value={review[field]}
          showScore={showScore}
        />
      ))}
    </Box>
  );
};

interface ReviewCriteriaRatingFieldProps {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  error?: string;
  onChange: (value: number | null) => void;
}

export const ReviewCriteriaRatingField: React.FC<ReviewCriteriaRatingFieldProps> = ({
  icon,
  label,
  value,
  error,
  onChange,
}) => {
  const theme = useTheme();
  const displayValue = value && value > 0 ? value : 0;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: "1px solid",
        borderColor: error
          ? theme.palette.error.main
          : alpha(theme.palette.primary.main, 0.12),
        height: "100%",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 800,
            color: displayValue > 0 ? "primary.main" : "text.disabled",
            flexShrink: 0,
          }}
        >
          {displayValue > 0 ? displayValue.toFixed(1) : "—"} / 5
        </Typography>
      </Stack>
      <Rating
        value={value}
        onChange={(_, v) => onChange(v)}
        precision={0.5}
        size="large"
        sx={{ fontSize: "1.75rem" }}
      />
      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

interface ReviewCriteriaRatingFieldsProps {
  values: Partial<
    Pick<
      SellerReview,
      | "reliability_rating"
      | "response_speed_rating"
      | "engineer_efficiency_rating"
      | "honesty_rating"
    >
  >;
  errors: Record<string, string>;
  onChange: (field: ReviewCriteriaField, value: number | null) => void;
}

export const ReviewCriteriaRatingFields: React.FC<ReviewCriteriaRatingFieldsProps> = ({
  values,
  errors,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={2}>
      {REVIEW_CRITERIA.map(({ field, labelKey, labelDefault, Icon }) => (
        <Grid key={field} size={{ xs: 12, sm: 6 }}>
          <ReviewCriteriaRatingField
            icon={<Icon fontSize="small" />}
            label={t(labelKey, { defaultValue: labelDefault })}
            value={values[field]}
            error={errors[field]}
            onChange={(v) => onChange(field, v)}
          />
        </Grid>
      ))}
    </Grid>
  );
};
